import { useContext, useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import {
  Block,
  Button,
  ConfirmModal,
  FlexContainer,
  FullscreenSheet,
  H4,
  Modal,
  ScannerInput,
  SoundNotificationContext,
  Span,
  TableSimple,
  Timer,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useTranslation } from 'react-i18next';

import { acceptBagInManifest, closeAcceptanceAct, getNotAcceptedManifestBags } from '../api';
import {
  AcceptanceActChangedHubPayload,
  AcceptanceBagScannedHubPayload,
  AcceptanceBagStatus,
  isSameUser,
  ManifestOperationResultStatus,
  NotAcceptedManifestBagsHttpResponse,
} from '../model/manifestAcceptance';
import { useManifestAcceptanceStore } from '../model/useManifestAcceptanceStore';
import ManifestAcceptanceScanBanner, { ScanBannerTone } from './ManifestAcceptanceScanBanner';
import { KeycloakContext } from '@shared/config/context/KeycloakContext';
import { ensureStarted, getSortingCenterHub } from '@shared/config/signalR/SortingSettingsHub';
import './AcceptanceScanning.scss';

type Props = {
  onClose: () => void;
  onRefetch?: () => void;
};

type ScanBanner = { tone: ScanBannerTone; bagNumber: string; message: string };

type BagRow = { id: string; number: string; kind: string; acceptedBy: string; isUndeclared: boolean };

const UNDECLARED_COLOR = '#8a5a00';

/**
 * Экран сканирования мешков в приёмку. Пики других операторов по этой же приёмке приходят по SignalR
 * и добавляются в список; если приёмку закрыл другой оператор — экран закрывается у всех.
 */
export default function AcceptanceScanning({ onClose, onRefetch }: Props) {
  const { t, i18n } = useTranslation();
  const { currentAcceptance, bagsData } = useManifestAcceptanceStore();
  const { onSoundSuccess, onSoundError, onSoundAlready, onSoundNotFound } = useContext(SoundNotificationContext);
  const { userInfo } = useContext(KeycloakContext);
  const { createNotification } = useNotificationsV2();
  const [scanBanner, setScanBanner] = useState<ScanBanner | null>(null);
  const [isOpenNotAcceptedModal, setIsOpenNotAcceptedModal] = useState(false);
  const [isLoadingNotAccepted, setIsLoadingNotAccepted] = useState(false);
  const [notAcceptedBags, setNotAcceptedBags] = useState<NotAcceptedManifestBagsHttpResponse | null>(null);
  const [isOpenFinishConfirm, setIsOpenFinishConfirm] = useState(false);
  const [isDisabledFinishConfirm, setIsDisabledFinishConfirm] = useState(true);

  const acceptanceActId = currentAcceptance?.acceptanceActId;
  const manifestNumber = currentAcceptance?.manifestNumber ?? null;

  // refs, чтобы подписка на хаб жила один раз и видела актуальную приёмку и пользователя
  const acceptanceActIdRef = useRef<number | undefined>(acceptanceActId);
  const currentUserIdRef = useRef<string | undefined>(userInfo?.sub);
  const onCloseRef = useRef(onClose);
  const onRefetchRef = useRef(onRefetch);

  useEffect(() => {
    acceptanceActIdRef.current = acceptanceActId;
    setScanBanner(null);
    setIsOpenFinishConfirm(false);
  }, [acceptanceActId]);

  useEffect(() => {
    currentUserIdRef.current = userInfo?.sub;
  }, [userInfo?.sub]);

  useEffect(() => {
    onCloseRef.current = onClose;
    onRefetchRef.current = onRefetch;
  }, [onClose, onRefetch]);

  // Берём состояние из стора напрямую: обработчики хаба не пересоздаются и не должны держать устаревший bagsData.
  const addBagRow = (bagNumber: string, isUndeclared: boolean, acceptedByLogin?: string | null) => {
    const store = useManifestAcceptanceStore.getState();
    if (store.bagsData.some((bag) => bag.number === bagNumber)) return;
    store.handleChangeBagsData([
      {
        number: bagNumber,
        isUndeclared,
        acceptedBy: acceptedByLogin ? { login: acceptedByLogin } : undefined,
        acceptanceDate: new Date().toISOString(),
      },
      ...store.bagsData,
    ]);
  };

  useEffect(() => {
    const conn = getSortingCenterHub();

    const onBagScanned = (payload: AcceptanceBagScannedHubPayload) => {
      if (!payload || payload.acceptanceActId !== acceptanceActIdRef.current) return;
      // Свои пики уже добавлены ответом на запрос — эхо из хаба пропускаем.
      if (isSameUser(payload.userId, currentUserIdRef.current)) return;

      const isUndeclared = payload.status === AcceptanceBagStatus.AcceptedUndeclared;
      addBagRow(payload.bagNumber, isUndeclared, payload.userLogin);
      createNotification({
        type: isUndeclared ? 'warning' : 'success',
        message: {
          title: t('manifestAcceptance.scanning.scannedByOther', {
            login: payload.userLogin ?? '',
            number: payload.bagNumber,
          }),
          desc: isUndeclared ? t('manifestAcceptance.scan.undeclaredTitle') : undefined,
        },
      });
    };

    const onActClosed = (payload: AcceptanceActChangedHubPayload) => {
      if (!payload || payload.acceptanceActId !== acceptanceActIdRef.current) return;
      if (isSameUser(payload.userId, currentUserIdRef.current)) return;

      createNotification({
        type: 'warning',
        message: {
          title: t('manifestAcceptance.scanning.closedByOther.title'),
          desc: t('manifestAcceptance.scanning.closedByOther.desc', { login: payload.userLogin ?? '' }),
        },
      });
      onSoundError?.();
      onRefetchRef.current?.();
      onCloseRef.current();
    };

    conn.on('AcceptanceBagScanned', onBagScanned);
    conn.on('AcceptanceActClosed', onActClosed);

    (async () => {
      await ensureStarted(conn);
    })();

    return () => {
      conn.off('AcceptanceBagScanned', onBagScanned);
      conn.off('AcceptanceActClosed', onActClosed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Данные тянем на каждое открытие: пока модалка закрыта, оператор сканирует мешки,
  // и закэшированный список успел бы устареть.
  const handleOpenNotAcceptedBags = async () => {
    if (!manifestNumber) return;

    try {
      setIsLoadingNotAccepted(true);
      const res = await getNotAcceptedManifestBags(manifestNumber);
      setNotAcceptedBags(res);
      setIsOpenNotAcceptedModal(true);
    } catch {
      createNotification({
        type: 'error',
        message: {
          title: t('common.systemError.title'),
          desc: t('common.systemError.desc'),
        },
      });
      onSoundError?.();
    } finally {
      setIsLoadingNotAccepted(false);
    }
  };

  const handleCloseNotAcceptedBags = () => {
    setIsOpenNotAcceptedModal(false);
    setNotAcceptedBags(null);
  };

  const errorMessageByStatus: Partial<Record<AcceptanceBagStatus, string>> = {
    [AcceptanceBagStatus.NotFound]: t('manifestAcceptance.scan.notFound'),
    [AcceptanceBagStatus.UserNotFound]: t('manifestAcceptance.scan.userNotFound'),
    [AcceptanceBagStatus.AcceptanceActNotFound]: t('manifestAcceptance.scan.acceptanceActNotFound'),
    [AcceptanceBagStatus.AcceptanceActAlreadyClosed]: t('manifestAcceptance.scan.acceptanceActAlreadyClosed'),
    [AcceptanceBagStatus.InvalidBagNumber]: t('manifestAcceptance.scan.invalidBagNumber'),
    [AcceptanceBagStatus.ManifestNumberScanned]: t('manifestAcceptance.scan.manifestNumberScanned'),
  };

  const { mutateAsync: onScanBag, isPending: isPendingScan } = useMutation({
    mutationKey: ['manifest-acceptance-scan-bag'],
    mutationFn: async (bagNumber: string) => {
      if (!acceptanceActId) throw new Error('acceptanceActId is missing');
      return { bagNumber, res: await acceptBagInManifest(bagNumber, { acceptanceActId }) };
    },
    onSuccess: ({ bagNumber, res }) => {
      const myLogin = userInfo?.preferred_username;

      switch (res.status) {
        case AcceptanceBagStatus.Accepted:
          setScanBanner({ tone: 'success', bagNumber, message: t('manifestAcceptance.scan.accepted') });
          addBagRow(bagNumber, false, myLogin);
          onSoundSuccess?.();
          return;

        case AcceptanceBagStatus.AcceptedUndeclared:
          // Мешок принят, но ни в одном манифесте его нет: отдельный звук и тост, чтобы оператор заметил,
          // при этом работа не останавливается — мешок физически приехал.
          setScanBanner({ tone: 'warning', bagNumber, message: t('manifestAcceptance.scan.acceptedUndeclared') });
          addBagRow(bagNumber, true, myLogin);
          createNotification({
            type: 'warning',
            message: {
              title: t('manifestAcceptance.scan.undeclaredTitle'),
              desc: t('manifestAcceptance.notifications.undeclaredBag.desc', { number: bagNumber }),
            },
          });
          onSoundNotFound?.();
          return;

        case AcceptanceBagStatus.Already:
          setScanBanner({ tone: 'error', bagNumber, message: t('manifestAcceptance.scan.alreadyAccepted') });
          createNotification({
            type: 'error',
            message: {
              title: t('manifestAcceptance.scan.alreadyTitle'),
              desc: t('manifestAcceptance.notifications.alreadyBag.desc', { number: bagNumber }),
            },
          });
          onSoundAlready?.();
          return;

        case AcceptanceBagStatus.AcceptanceActAlreadyClosed: {
          // Приёмку закрыли, пока оператор сканировал — выходим из экрана.
          const message = t('manifestAcceptance.scan.acceptanceActAlreadyClosed');
          setScanBanner({ tone: 'error', bagNumber, message });
          createNotification({
            type: 'error',
            message: { title: t('manifestAcceptance.scan.rejectedTitle'), desc: message },
          });
          onSoundError?.();
          onRefetch?.();
          onClose();
          return;
        }

        default: {
          const message = errorMessageByStatus[res.status] ?? t('manifestAcceptance.scan.unexpectedError');
          setScanBanner({ tone: 'error', bagNumber, message });
          createNotification({
            type: 'error',
            message: { title: t('manifestAcceptance.scan.rejectedTitle'), desc: message },
          });
          onSoundError?.();
        }
      }
    },
    onError: (error) => {
      setScanBanner({
        tone: 'error',
        bagNumber: '',
        message: error instanceof Error ? error.message : t('manifestAcceptance.scan.unexpectedError'),
      });
      onSoundError?.();
    },
  });

  const { mutateAsync: onFinishAct, isPending: isPendingFinish } = useMutation({
    mutationKey: ['close-acceptance-act-from-scanning'],
    mutationFn: closeAcceptanceAct,
    onSuccess: (res) => {
      setIsOpenFinishConfirm(false);

      if (res.status === ManifestOperationResultStatus.Success) {
        createNotification({
          type: 'success',
          message: {
            title: t('manifestAcceptance.notifications.actClosed.title'),
            desc: t('manifestAcceptance.notifications.actClosed.desc'),
          },
        });
        onSoundSuccess?.();
        onRefetch?.();
        onClose();
        return;
      }

      // Интеграция с SortCenter API упала, но локально приёмка уже закрыта.
      if (res.status === ManifestOperationResultStatus.HasErrorApiIntegration) {
        createNotification({
          type: 'warning',
          message: {
            title: t('manifestAcceptance.notifications.apiIntegration.title'),
            desc: t('manifestAcceptance.notifications.apiIntegration.desc'),
          },
        });
        onRefetch?.();
        onClose();
        return;
      }

      createNotification({
        type: 'warning',
        message: {
          title: t('manifestAcceptance.notifications.operationError.title'),
          desc: t('manifestAcceptance.notifications.operationError.desc'),
        },
      });
      onSoundError?.();
    },
    onError: () => {
      setIsOpenFinishConfirm(false);
      createNotification({
        type: 'error',
        message: {
          title: t('common.systemError.title'),
          desc: t('common.systemError.desc'),
        },
      });
      onSoundError?.();
    },
  });

  const handleOpenFinishConfirm = () => {
    setIsDisabledFinishConfirm(true);
    setIsOpenFinishConfirm(true);
  };

  const headerTitle = currentAcceptance
    ? `${t('manifestAcceptance.scanning.acceptanceNumber')} ${currentAcceptance.acceptanceNumber ?? ''}`.trim()
    : '';
  const headerSubtitle = manifestNumber
    ? `${t('tableHeader.manifestNumber')}: ${manifestNumber}`
    : t('manifestAcceptance.scanning.noManifest');

  const rows: BagRow[] = bagsData.map((bag, index) => ({
    id: `${bag.number}-${index}`,
    number: bag.number,
    kind: bag.isUndeclared ? t('manifestAcceptance.scan.kindUndeclared') : t('manifestAcceptance.scan.kindDeclared'),
    acceptedBy: bag.acceptedBy?.login ?? '-',
    isUndeclared: !!bag.isUndeclared,
  }));

  return (
    <FullscreenSheet height={'100dvh'} open={currentAcceptance !== null} onClose={onClose}>
      <div className="manifest-scan-header">
        <Block className="manifest-scan-header__title">
          <FlexContainer direction="column" gap="2px">
            <Span fw={600}>{headerTitle}</Span>
            <Span>{headerSubtitle}</Span>
          </FlexContainer>
        </Block>
        <div className="manifest-scan-header__actions">
          {manifestNumber && (
            <Button
              type="secondary"
              onClick={handleOpenNotAcceptedBags}
              loading={isLoadingNotAccepted}
              disabled={isLoadingNotAccepted}
            >
              {t('manifestAcceptance.notAccepted.action')}
            </Button>
          )}
          <Button type="secondary" onClick={handleOpenFinishConfirm} disabled={isPendingFinish}>
            {t('manifestAcceptance.actions.finishAcceptance')}
          </Button>
          <Button onClick={onClose} width="120px">
            {t('tableHeader.rollUp')}
          </Button>
        </div>
      </div>

      <Block mt={16} mb={16}>
        <ScannerInput
          buttonLabel={t('send')}
          lang={i18n.language}
          isLoading={isPendingScan}
          disabled={isPendingScan || !acceptanceActId}
          label={t('inputNumber')}
          onSubmit={(bagNumber) => onScanBag(bagNumber)}
        />
      </Block>

      {scanBanner && (
        <Block mb={16}>
          <ManifestAcceptanceScanBanner
            tone={scanBanner.tone}
            bagNumber={scanBanner.bagNumber}
            message={scanBanner.message}
          />
        </Block>
      )}

      <TableSimple<BagRow>
        enableColumnResizing={false}
        columns={[
          {
            header: t('tableHeader.bagNumber'),
            accessor: 'number',
            textAlign: 'center',
            renderCell: (row) =>
              row.isUndeclared ? (
                <span style={{ color: UNDECLARED_COLOR, fontWeight: 600 }}>{row.number}</span>
              ) : (
                row.number
              ),
          },
          {
            header: t('tableHeader.status'),
            accessor: 'kind',
            textAlign: 'center',
            renderCell: (row) =>
              row.isUndeclared ? (
                <span style={{ color: UNDECLARED_COLOR, fontWeight: 600 }}>{row.kind}</span>
              ) : (
                row.kind
              ),
          },
          { header: t('tableHeader.userName'), accessor: 'acceptedBy', textAlign: 'center' },
        ]}
        data={rows}
        language={i18n.language}
        withOutPagination
        mobileWidth={680}
        maxHeightTable="calc(100vh - 360px)"
        isLoading={false}
        emptyPlaceholder={t('noData')}
      />

      <ConfirmModal
        onSubmit={() => acceptanceActId && onFinishAct(acceptanceActId)}
        lang={i18n.language}
        desc={
          <FlexContainer direction="column" align="center" justify="center">
            <Block mb={8}>
              <Span>{t('manifestAcceptance.acceptanceActs.closeConfirm.desc')}</Span>
            </Block>
            <Timer onComplete={() => setIsDisabledFinishConfirm(false)} durationMs={5000} />
          </FlexContainer>
        }
        isOpen={isOpenFinishConfirm}
        disabled={isDisabledFinishConfirm}
        isLoading={isPendingFinish}
        onClose={() => setIsOpenFinishConfirm(false)}
      />

      <Modal
        width={{ max: '600px', min: '300px' }}
        isOpen={isOpenNotAcceptedModal}
        onClose={handleCloseNotAcceptedBags}
      >
        <Block mb={16}>
          <H4 align="center">
            {t('manifestAcceptance.notAccepted.title')} {notAcceptedBags?.manifestNumber}
          </H4>
        </Block>
        <Block mb={16}>
          <FlexContainer direction="column" gap="8px">
            <Span>
              {t('manifestAcceptance.notAccepted.total')}: {notAcceptedBags?.totalCount ?? 0}
            </Span>
            <Span>
              {t('manifestAcceptance.notAccepted.accepted')}: {notAcceptedBags?.acceptedCount ?? 0}
            </Span>
            <Span fw={600}>
              {t('manifestAcceptance.notAccepted.remaining')}: {notAcceptedBags?.notAcceptedCount ?? 0}
            </Span>
          </FlexContainer>
        </Block>
        <TableSimple<{ id: string; number: string }>
          enableColumnResizing={false}
          columns={[{ header: t('tableHeader.bagNumber'), accessor: 'number', textAlign: 'center' }]}
          data={(notAcceptedBags?.bags ?? []).map((bag, index) => ({
            id: `${bag.number}-${index}`,
            number: bag.number,
          }))}
          language={i18n.language}
          withOutPagination
          mobileWidth={680}
          maxHeightTable="calc(100dvh - 320px)"
          isLoading={false}
          emptyPlaceholder={t('manifestAcceptance.notAccepted.empty')}
        />
      </Modal>
    </FullscreenSheet>
  );
}
